<template>
  <div>
    <div>
      <div class="font-weight-bold mb-1 mt-5">
        Experience
      </div>
      <h2 class="mb-5">
        What professional experience do you have?
      </h2>
    </div>
    <div v-if="isFetchingUserExperience">
      <v-skeleton-loader v-for="i in 3" :key="i" type="list-item" />
    </div>
    <div
      v-else
      v-for="experience in currentUserExperience"
      :key="experience.id"
      class="d-flex justify-space-between mb-5"
    >
      <div class="experience_summary">
        <svg
          width="32"
          height="28"
          viewBox="0 0 32 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line
            x1="1"
            y1="27"
            x2="31"
            y2="27"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M4 3C4 1.89543 4.89543 1 6 1H14C15.1046 1 16 1.89543 16 3V27H4V3Z"
            stroke="black"
            stroke-width="2"
          />
          <path
            d="M16 9H26C27.1046 9 28 9.89543 28 11V27H16V9Z"
            stroke="black"
            stroke-width="2"
          />
          <rect
            x="7.5"
            y="4.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="7.5"
            y="12.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="7.5"
            y="8.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="7.5"
            y="16.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="7.5"
            y="20.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="11.5"
            y="4.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="11.5"
            y="12.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="11.5"
            y="8.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="11.5"
            y="16.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="23.5"
            y="12.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="19.5"
            y="12.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="11.5"
            y="20.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="23.5"
            y="16.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="23.5"
            y="20.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="19.5"
            y="16.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
          <rect
            x="19.5"
            y="20.5"
            width="1"
            height="1"
            rx="0.5"
            fill="#0A70F5"
            stroke="black"
          />
        </svg>
        <div class="experience_summary__text">
          <div class="experience_summary__text__title">
            {{ experience.jobTitle }} - {{ experience.organisation }}
          </div>
          <div class="experience_summary__text__subtitle">
            {{ experience.startDate | baseDateFormat }}
            - {{ experience.endDate | baseDateFormat }}
          </div>
        </div>
      </div>
      <div class="d-flex align-center">
        <EditExperienceAction :experience="experience" class="mr-2" />
        <div class="text-center " v-if="includes(itemsToRemove, experience.id)">
          <v-progress-circular size="30" indeterminate color="primary" />
        </div>
        <div @click="() => onRemove(experience.id)" v-else>
          <v-icon>mdi-close</v-icon>
        </div>
      </div>
    </div>
    <div
      v-if="showValidationErrors && !currentUserExperience.length"
      class="text-error mt-5"
    >
      You have to add at least 1 experience
    </div>
    <AddExperienceAction />
    <div class="d-flex justify-end">
      <PrimaryButton @click.native="nextStep">
        {{ submitButtonTitle || "Continue" }}
      </PrimaryButton>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { EXPERIENCE_NAMESPACE } from "@/store/modules/users/experience";
import { createNamespacedHelpers } from "vuex";
import {
  FETCH_USER_EXPERIENCE,
  REMOVE_USER_EXPERIENCE
} from "@/store/modules/users/experience/actions";
import { copyState } from "@/utils/helpers";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { filter, includes, size } from "lodash";
import EditExperienceAction from "@/views/profile/QuestionsAndAnswers/steps/Experience/actions/EditExperienceAction";
import AddExperienceAction from "@/views/profile/QuestionsAndAnswers/steps/Experience/actions/AddExperienceAction";
import { formatDate } from "@/utils/time";
import { BASE_DATE_FORMAT } from "@/constants/common";

const { mapActions, mapState: mapExperienceState } = createNamespacedHelpers(
  EXPERIENCE_NAMESPACE
);
const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);

export default {
  name: "Experience",
  components: {
    PrimaryButton,
    EditExperienceAction,
    AddExperienceAction
  },
  props: {
    submitButtonTitle: String
  },
  created() {
    this.emitProgressValue(100);
    this.fetchExperience(this.self.id);
  },
  data() {
    return {
      itemsToRemove: [],
      showValidationErrors: false
    };
  },
  computed: {
    ...mapAuthState({
      self: state => state.userData
    }),
    ...mapExperienceState(
      copyState(["isFetchingUserExperience", "currentUserExperience"])
    )
  },
  methods: {
    ...mapActions({
      removeExperience: REMOVE_USER_EXPERIENCE,
      fetchExperience: FETCH_USER_EXPERIENCE
    }),
    async onRemove(idToRemove) {
      this.itemsToRemove = [...this.itemsToRemove, idToRemove];
      await this.removeExperience(idToRemove);
      this.itemsToRemove = filter(this.itemsToRemove, idToRemove);
    },
    nextStep() {
      this.showValidationErrors = true;
      const isValid = size(this.currentUserExperience);
      if (isValid) {
        // progressValue bigger than 100 to trigger next step function
        this.emitProgressValue(101);
        this.$emit("close");
      }
    },
    emitProgressValue(value) {
      this.$emit("onStepChanged", {
        progressValue: value
      });
    },
    stepBack() {
      this.emitProgressValue(0);
    },
    includes
  },
  filters: {
    baseDateFormat(date) {
      return date === "Present"
        ? date
        : formatDate({ date, endFormat: BASE_DATE_FORMAT });
    }
  }
};
</script>

<style lang="scss">
.experience_summary {
  svg {
    float: left;
    margin-top: 5px;
    margin-right: 10px;
  }

  &__text {
    float: left;

    &__title {
      font-weight: bold;
    }

    &__subtitle {
      color: $secondary-text;
    }
  }
}
</style>
